import clsx from "clsx";
import _throttle from "lodash/throttle";
import React, { useEffect, useRef, useState } from "react";
import FeedCountAndSort from "web/react/components/feed-count-and-sort/feed-count-and-sort";
import { FilterContainerEmo } from "web/react/components/filters-emo/filter-container-emo";
import environment from "web/script/modules/environment";
import { ProductFeedSerializer } from "web/types/serializers";
import { UniversalFiltersData } from "web/types/universal-filters";
import * as styles from "./feed.css";

interface FilterBarProps {
    universalFilters: UniversalFiltersData;
    productCount: number;
    retailerCount: number;
    productFeed: ProductFeedSerializer;
    blurFeed: boolean;
}

export function FilterBar({
    universalFilters,
    productCount,
    retailerCount,
    productFeed,
    blurFeed,
}: FilterBarProps): React.ReactElement {
    // ENRICH-3289
    // Stick the filters at the top of the viewport
    // `control` - no sticky
    // `sticky` - stick to the top of the viewport
    // `onscroll` - stick to the top but show filters only on scroll up
    const [scroll, setScroll] = useState("");
    const [isSticky, setIsSticky] = useState(false);
    const [prevScrollTop, setPrevScrollTop] = useState(0);
    const filtersRef = useRef<HTMLDivElement>(null);
    const filtersBarOffsetTop = useRef(0);

    const stickyBarFlag = String(environment.getFeature("act_sticky_feed_filters"));

    useEffect(() => {
        if (stickyBarFlag !== "onscroll") {
            return;
        }

        if (!filtersBarOffsetTop.current) {
            filtersBarOffsetTop.current = filtersRef.current?.offsetTop || 0;
        }

        function onWindowScroll(): void {
            const scrollTop = window.scrollY;

            if (scrollTop < prevScrollTop) {
                setScroll("up");
            } else {
                setScroll("down");
            }

            setPrevScrollTop(scrollTop);
            setIsSticky(filtersBarOffsetTop.current <= scrollTop);
        }

        const throttledOnWindowScroll = _throttle(onWindowScroll, 100);

        window.addEventListener("scroll", throttledOnWindowScroll);

        return () => {
            window.removeEventListener("scroll", throttledOnWindowScroll);
        };
    }, [stickyBarFlag, prevScrollTop]);

    return (
        <div
            ref={filtersRef}
            className={clsx({
                [styles.filtersSticky]: ["sticky", "onscroll"].includes(stickyBarFlag),
                [styles.filtersStickyHidden]:
                    stickyBarFlag === "onscroll" && scroll === "down" && isSticky,
            })}
        >
            <div className={clsx(styles.feed)}>
                <div className={clsx(styles.feedGrid, { [styles.blurFeed]: blurFeed })}>
                    <FilterContainerEmo
                        filters={universalFilters}
                        productCount={productCount}
                        sortOptions={productFeed.feed_sort_dropdown.sort_options}
                        shouldCloseDropdown={
                            stickyBarFlag === "onscroll" && scroll === "down" && isSticky
                        }
                    />

                    <FeedCountAndSort
                        productCount={productCount}
                        retailerCount={retailerCount}
                        sortOptions={productFeed.feed_sort_dropdown.sort_options}
                    />
                </div>
            </div>
        </div>
    );
}
